import { action, observable } from 'mobx';
import { Model, model, modelAction, onSnapshot, fromSnapshot, prop, SnapshotInOf, SnapshotOutOf } from 'mobx-keystone';

import { noop } from '../utils';
import IHenshuDataStore, { IEachList } from './IHenshuDataStore';
import IHenshuPersistLayer from '../persist/IHenshuPersistLayer';


export default class KeystoneDataStore<T extends object> implements IHenshuDataStore {
    
    @observable
    instance: T;

    seeds: any = {};

    private _onSnapshot: Function = noop;

    constructor(klass: { new (obj: any): T }, seeds?: any) {
        this.instance = new klass({});

        if (seeds)  {
            this.seeds = seeds;
        }

        this._subscribeToSnapshot();
    }

    onSnapshot(callback: Function) {
        this._onSnapshot = callback;
    }

    @action
    applySnapshot(snapshot: object) {
        if (Object.keys(snapshot).length > 0) {
            this.instance = fromSnapshot<T>(snapshot as SnapshotInOf<T>);
            this._subscribeToSnapshot();
        }
    }

    async seed(persist: IHenshuPersistLayer) {
        if (persist.lang in this.seeds) {
            const seed = async () => await this.seeds[persist.lang](this.instance);
            if (persist.batch) {
                await persist.batch(seed);
            }
            else {
                await seed();
            }
        }
    }

    private _subscribeToSnapshot() {
        onSnapshot(this.instance, (snapshot: SnapshotOutOf<T>) => {
            this._onSnapshot(snapshot as object);
        });
    }

}


export function createKeystoneEachList<T>(klass: { new (obj: any): T }, data: object) {
    @model('henshu/KeystoneEachList')
    class KeystoneEachList<T> extends Model({
        items: prop<any[]>(() => [])
    }) implements IEachList<T> {

        @modelAction
        add(item: T) {
            this.items.push(item);
        }

        @modelAction
        create(index?: number) {
            const item = new klass({});

            if (index) {
                this.items.splice(index, 0, item);
            }
            else {
                this.items.push(item);
            }
        }

        @modelAction
        move(item: T, index: number) {
            this.items.splice(this.items.indexOf(item), 1);
            // @ts-ignore
            this.items.splice(index, 0, new klass(JSON.parse(JSON.stringify(item.$))));
        }

        @modelAction
        remove(item: T) {
            this.items.splice(this.items.indexOf(item), 1);
        }

    }

    return new KeystoneEachList<T>(data);
}
